import formatMoney from "../../common/formatters/money";
import { getConfigValue } from "../api/price";
import { useOfferCalculatorDispatch, useOfferCalculatorState, } from "../context/OfferCalculatorContext";
import { clampStepperValue, getTaxLabel, parseMoneyInput, } from "../utils/configHelpers";
import React, { useEffect, useRef, useState } from "react";
const STEPPER_DEBOUNCE_MS = 300;
const minusIcon = (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M19 11C19.5523 11 20 11.4477 20 12C20 12.5523 19.5523 13 19 13H5C4.44772 13 4 12.5523 4 12C4 11.4477 4.44772 11 5 11H19Z" fill="currentColor"/>
  </svg>);
const plusIcon = (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 4C12.5523 4 13 4.44772 13 5V11H19C19.5523 11 20 11.4477 20 12C20 12.5523 19.5523 13 19 13H13V19C13 19.5523 12.5523 20 12 20C11.4477 20 11 19.5523 11 19V13H5C4.44772 13 4 12.5523 4 12C4 11.4477 4.44772 11 5 11H11V5C11 4.44772 11.4477 4 12 4Z" fill="currentColor"/>
  </svg>);
function StepperField({ name, title, }) {
    var _a, _b, _c, _d, _e, _f;
    const state = useOfferCalculatorState();
    const dispatch = useOfferCalculatorDispatch();
    const field = getConfigValue(state.config, name);
    const value = Number(state[name] || 0);
    const tax = getTaxLabel(state.finance_type, state.offer_type);
    const [localValue, setLocalValue] = useState(null);
    const [inputValue, setInputValue] = useState(() => formatMoney(value));
    const debounceRef = useRef(null);
    const currentValue = localValue !== null && localValue !== void 0 ? localValue : value;
    // Sync committed value back to the input — but skip if the user is still
    // mid-interaction (debounce pending). A concurrent price recalculation from
    // another field can update `value` and would otherwise discard the edit.
    useEffect(() => {
        if (debounceRef.current !== null)
            return;
        setLocalValue(null);
        setInputValue(formatMoney(value));
    }, [value]);
    useEffect(() => () => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
        }
    }, []);
    const scheduleCommit = (next) => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null;
            dispatch({ type: "set_param", name, value: String(next) });
            setLocalValue(null);
        }, STEPPER_DEBOUNCE_MS);
    };
    if (!field || field.options === null) {
        return null;
    }
    const min = field.options && typeof field.options === "object" && "min" in field.options
        ? Number(field.options.min)
        : ((_b = (_a = field.steps) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : 0);
    const max = field.options && typeof field.options === "object" && "max" in field.options
        ? Number(field.options.max)
        : ((_d = (_c = field.steps) === null || _c === void 0 ? void 0 : _c[field.steps.length - 1]) !== null && _d !== void 0 ? _d : min);
    const step = (_e = field.step) !== null && _e !== void 0 ? _e : (name === "mileage" ? 10000 : 500);
    const disabled = Boolean(field.disabled);
    const suffix = name === "mileage" ? "km" : "zł";
    const commitValue = (raw) => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        const next = clampStepperValue(parseMoneyInput(raw), min, max, step);
        setLocalValue(null);
        setInputValue(formatMoney(next));
        if (next === value) {
            return;
        }
        dispatch({ type: "set_param", name, value: String(next) });
    };
    const adjust = (delta) => {
        const next = clampStepperValue(currentValue + delta, min, max, step);
        setLocalValue(next);
        setInputValue(formatMoney(next));
        scheduleCommit(next);
    };
    return (<div className={`config-group group-${name} notranslate`}>
      <div className="mb-4 text-base font-medium leading-5">
        {title}
        {name !== "mileage" ? (<>
            {" "}
            (<span className="texts-tax">{tax}</span>)
          </>) : null}
      </div>
      {disabled && ((_f = field.params) === null || _f === void 0 ? void 0 : _f.disabledMessage) ? (<p className="text-sm font-medium text-[#66676C] lg:text-center">
          {field.params.disabledMessage}
        </p>) : (<>
          <div className="flex items-start justify-center gap-1.5 self-stretch">
            <button type="button" disabled={disabled || currentValue <= min} onClick={() => adjust(-step)} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-[#A1A2A5] text-[#0071CE] transition disabled:opacity-40" aria-label="Poprzednia wartość">
              {minusIcon}
            </button>
            <label className="flex min-w-0 max-h-12 flex-1 items-center gap-2 self-stretch rounded-md border border-[#9BABB0] bg-white px-4 py-3.5">
              <input type="text" inputMode="numeric" disabled={disabled} value={inputValue} onChange={(event) => {
                if (debounceRef.current !== null) {
                    clearTimeout(debounceRef.current);
                    debounceRef.current = null;
                }
                setLocalValue(null);
                setInputValue(event.target.value);
            }} onFocus={(event) => event.target.select()} onBlur={(event) => commitValue(event.target.value)} onKeyDown={(event) => {
                if (event.key === "Enter") {
                    event.currentTarget.blur();
                }
            }} className="w-full min-w-0 border-0 bg-transparent p-0 text-left text-base font-normal leading-[2.2rem] text-[#5A6C72] outline-none focus:ring-0" aria-label={title}/>
              <span className="shrink-0 text-base font-normal leading-[2.2rem] text-[#405359]">
                {suffix}
              </span>
            </label>
            <button type="button" disabled={disabled || currentValue >= max} onClick={() => adjust(step)} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-[#A1A2A5] text-[#0071CE] transition disabled:opacity-40" aria-label="Następna wartość">
              {plusIcon}
            </button>
          </div>
          {name !== "mileage" ? (<p className="mt-1.5 text-xs tracking-wide text-[#405359]">
              Min. {formatMoney(min)} zł – Maks. {formatMoney(max)} zł
            </p>) : null}
        </>)}
    </div>);
}
export default function DownpaymentStepper() {
    const state = useOfferCalculatorState();
    return (<>
      <StepperField name="contribution" title="Wkład własny"/>
      {state.finance_type === "leasing" && (<StepperField name="repurchase" title="Wartość wykupu"/>)}
      <StepperField name="mileage" title="Roczny limit kilometrów"/>
    </>);
}
